import { useMemo, useState } from 'react';
import { AlertTriangle, PackageSearch } from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import { useInventoryAllocations } from '../hooks/useInventoryAllocations';

function formatDate(date: string | null): string {
  if (!date) return '\u2014';
  const parsed = parseISO(date);
  if (!isValid(parsed)) return '\u2014';
  return format(parsed, 'MMM d, yyyy');
}

export function InventoryPage() {
  const [partSearch, setPartSearch] = useState('');
  const trimmedSearch = partSearch.trim();

  const { data, isLoading, error } = useInventoryAllocations({
    partNumber: trimmedSearch || undefined,
    limit: 200,
    offset: 0,
  });

  const rows = data?.data ?? [];

  const summary = useMemo(() => {
    let totalOnHand = 0;
    let totalAllocated = 0;
    let negativeAvailableCount = 0;

    for (const row of rows) {
      totalOnHand += row.gpOnHand + row.shadowInventory;
      totalAllocated += row.allocatedQuantity;
      if (row.effectiveAvailable < 0) negativeAvailableCount++;
    }

    return {
      partCount: rows.length,
      totalOnHand,
      totalAllocated,
      negativeAvailableCount,
    };
  }, [rows]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-56 bg-background-muted rounded animate-pulse" />
        <div className="h-10 w-full max-w-sm bg-background-muted rounded animate-pulse" />
        <div className="h-48 bg-background-muted rounded-lg animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-foreground">Inventory</h1>
        <div className="rounded-lg border border-danger bg-danger-subtle p-6">
          <p className="text-sm text-danger">
            Failed to load inventory: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Inventory</h1>
        <p className="text-sm text-foreground-muted mt-1">
          Current inventory, allocation totals, and project allocation detail
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-background rounded-lg border border-border p-4">
          <p className="text-xs text-foreground-muted">Parts</p>
          <p className="text-2xl font-semibold text-foreground mt-1">{summary.partCount}</p>
        </div>
        <div className="bg-background rounded-lg border border-border p-4">
          <p className="text-xs text-foreground-muted">Current On Hand</p>
          <p className="text-2xl font-semibold text-foreground mt-1">{summary.totalOnHand}</p>
        </div>
        <div className="bg-background rounded-lg border border-border p-4">
          <p className="text-xs text-foreground-muted">Allocated</p>
          <p className="text-2xl font-semibold text-foreground mt-1">{summary.totalAllocated}</p>
        </div>
        <div className="bg-background rounded-lg border border-border p-4">
          <p className="text-xs text-foreground-muted">Negative Available</p>
          <p className="text-2xl font-semibold text-danger mt-1">{summary.negativeAvailableCount}</p>
        </div>
      </div>

      <div className="bg-background rounded-lg border border-border p-4">
        <label htmlFor="part-search" className="text-xs text-foreground-muted block mb-2">
          Filter by part number
        </label>
        <input
          id="part-search"
          value={partSearch}
          onChange={(e) => setPartSearch(e.target.value)}
          placeholder="e.g. PN-100"
          className="w-full max-w-sm rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-foreground-subtle focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-background p-12 text-center">
          <PackageSearch className="h-10 w-10 text-foreground-muted mx-auto mb-3" />
          <p className="text-sm font-medium text-foreground-muted">No inventory rows found</p>
        </div>
      ) : (
        <div className="bg-background rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-4 py-2 text-xs font-medium text-foreground-muted">Part</th>
                  <th className="px-4 py-2 text-xs font-medium text-foreground-muted">GP On Hand</th>
                  <th className="px-4 py-2 text-xs font-medium text-foreground-muted">Shadow</th>
                  <th className="px-4 py-2 text-xs font-medium text-foreground-muted">Allocated</th>
                  <th className="px-4 py-2 text-xs font-medium text-foreground-muted">Effective Available</th>
                  <th className="px-4 py-2 text-xs font-medium text-foreground-muted">Allocated To Projects</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.partNumber} className="border-b border-border last:border-b-0 align-top">
                    <td className="px-4 py-3 font-mono text-foreground">{row.partNumber}</td>
                    <td className="px-4 py-3 text-foreground">{row.gpOnHand}</td>
                    <td className="px-4 py-3 text-foreground">{row.shadowInventory}</td>
                    <td className="px-4 py-3 text-foreground">{row.allocatedQuantity}</td>
                    <td className="px-4 py-3">
                      <span className={row.effectiveAvailable < 0 ? 'text-danger font-medium' : 'text-foreground'}>
                        {row.effectiveAvailable}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {row.projects.length === 0 ? (
                        <span className="text-foreground-subtle">\u2014</span>
                      ) : (
                        <div className="space-y-1 text-xs">
                          {row.projects.map((project) => (
                            <div key={`${row.partNumber}-${project.salesOrderId}`} className="text-foreground">
                              <span className="font-mono font-medium">{project.gpSalesOrderNumber}</span>
                              <span className="mx-1 text-foreground-subtle">\u00b7</span>
                              <span>{project.customerName}</span>
                              <span className="mx-1 text-foreground-subtle">\u00b7</span>
                              <span>{project.allocatedQuantity} allocated</span>
                              <span className="mx-1 text-foreground-subtle">\u00b7</span>
                              <span>{formatDate(project.earliestPromisedShipDate)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {summary.negativeAvailableCount > 0 && (
        <div className="rounded-lg border border-warning bg-warning-subtle p-3">
          <div className="flex items-center gap-2 text-sm text-warning-foreground">
            <AlertTriangle className="h-4 w-4" />
            <span>{summary.negativeAvailableCount} part numbers are currently over-allocated.</span>
          </div>
        </div>
      )}
    </div>
  );
}
